import React, {useEffect} from 'react'

import {Colors, Stack, Tab, Tabs} from '../../components/UI/index.js'
import RoutesTable from '../../components/routes/RoutesTable.js'
import RouteTemplates from './RouteTemplates.js'
import t from '../../helpers/Languages.js'
import type {Reseller} from '../../../server/functions/resellers/resellers.types.js'
import type {User} from '../../../server/functions/users/users.types.js'

type RoutesView = 'routes' | 'templates'

/** The Routes table is hidden for now: shown with localStorage.showRoutesPage = 'true' */
export function routesTableEnabled(): boolean {
    try {
        return localStorage.getItem('showRoutesPage') === 'true'
    } catch {
        return false
    }
}

/**
 * The tab of /routes in the URL (?view=templates), so the navigation can link to it. Routes without it. Without the
 * Routes table only the route templates.
 */
export function getRoutesView(search: string, templatesAvailable: boolean, routesTable: boolean): RoutesView {
    if (!routesTable) {
        return 'templates'
    }

    return templatesAvailable && new URLSearchParams(search).get('view') === 'templates' ? 'templates' : 'routes'
}

interface RoutesProps {
    reseller: Reseller
    onUpdateReseller: (reseller: Reseller, path: string) => void
    user: User
    // From react-router
    history: {push: (path: string) => void}
    location: {search: string}
}

/** /routes: the route templates (Koerier Plus) and, when enabled, the routes of a date range. Built like the Ritten view. */
export default function Routes({reseller, onUpdateReseller, user, history, location}: RoutesProps) {
    // Route templates are part of the Koerier Plus subscription
    const templatesAvailable = reseller.accountType === 'carrierPlus'
    const routesTable = routesTableEnabled()
    const view = getRoutesView(location.search, templatesAvailable, routesTable)

    useEffect(() => {
        document.title = `${view === 'templates' ? t('routeTemplates') : t('routes')} • ${reseller.settings.accountName || reseller.name || ''}`
    }, [view, reseller])

    return (
        <Stack style={{flex: 1}}>
            {templatesAvailable && routesTable && (
                <div style={{borderBottom: `1px solid ${Colors.border}`}}>
                    <Tabs
                        style={{width: 400, marginBottom: 2}}
                        value={view}
                        onChange={(event) => history.push(event.target.value === 'templates' ? '/routes?view=templates' : '/routes')}
                    >
                        <Tab style={{fontSize: 14, marginLeft: 16}} size='sm' value='routes'>
                            {t('routes')}
                        </Tab>
                        <Tab style={{fontSize: 14}} size='sm' value='templates'>
                            {t('routeTemplates')}
                        </Tab>
                    </Tabs>
                </div>
            )}

            {view === 'routes' && <RoutesTable reseller={reseller} onUpdateReseller={onUpdateReseller} history={history} />}

            {view === 'templates' && templatesAvailable && <RouteTemplates reseller={reseller} user={user} />}
        </Stack>
    )
}
